import type { AppContext, ComponentPublicInstance, DirectiveBinding } from "vue";
import { watch, type WatchStopHandle, type Ref } from "vue";

import { translate, ValueDictionary } from "../i18n";

type TranslateBindingValue = string | { key: string; options: ValueDictionary };

const watchers = new WeakMap<HTMLElement, WatchStopHandle>();

export const TRANSLATE_LANGUAGE_KEY = Symbol("translateLanguage");

const resolveLanguageRef = (binding: DirectiveBinding<TranslateBindingValue>): Ref<string> | null => {
  const instance = binding.instance as ComponentPublicInstance | null | undefined;
  if (!instance) {
    return null;
  }
  const internalInstance = instance.$ as { appContext?: AppContext } | undefined;
  const provides = internalInstance?.appContext?.provides;
  if (!provides) {
    return null;
  }
  const candidate: unknown = provides[TRANSLATE_LANGUAGE_KEY];
  if (candidate && typeof candidate === "object") {
    return candidate as Ref<string>;
  }
  return null;
};

const translateDirective = (el: HTMLElement, binding: DirectiveBinding<TranslateBindingValue>) => {
  const value = binding.value;
  let translation: string;
  if (typeof value === "string") {
    translation = translate(value);
  } else if (value.key) {
    translation = translate(value.key, value.options);
  } else {
    throw Error(`Bad translate binding value format: ${JSON.stringify(value)}`);
  }

  if (typeof binding.arg === "string") {
    el.setAttribute(binding.arg, translation);
  } else {
    el.innerHTML = translation;
  }
};

export default {
  mounted(el: HTMLElement, binding: DirectiveBinding<TranslateBindingValue>) {
    translateDirective(el, binding);

    const languageRef = resolveLanguageRef(binding);
    if (languageRef) {
      const stopWatch = watch(languageRef, () => {
        translateDirective(el, binding);
      });

      watchers.set(el, stopWatch);
    }
  },
  updated(el: HTMLElement, binding: DirectiveBinding<TranslateBindingValue>) {
    translateDirective(el, binding);
  },
  unmounted(el: HTMLElement) {
    const stopWatch = watchers.get(el);
    if (stopWatch) {
      stopWatch();
      watchers.delete(el);
    }
  }
};
